import { Combobox, InputBase, Text, useCombobox } from '@mantine/core'
import { useUncontrolled } from '@mantine/hooks'
import { type JSX, useState } from 'react'

type Props = {
  initialOptions: string[]

  label?: string
  required?: boolean
  placeholder?: string
  allowDeselect?: boolean
  className?: string

  // input props
  onChange: (value: string | null) => void
  value?: string | null
  defaultValue?: string | null
  error?: any
  onFocus?: any
  onBlur?: any
}

export function SelectCreatable({
  initialOptions,

  label,
  required,
  placeholder,
  allowDeselect = true,
  className,

  ...inputProps
}: Props): JSX.Element {
  const [selectOptions, setSelectOptions] = useState(initialOptions)

  const [currentValue, handleChange] = useUncontrolled(inputProps)
  const [search, setSearch] = useState<string | null>(null)

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const exactOptionMatch = selectOptions.some((item) => item === search)
  const filteredOptions =
    exactOptionMatch || search == null
      ? selectOptions
      : selectOptions.filter((item) =>
          item.toLowerCase().includes(search.toLowerCase().trim())
        )

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item} key={item}>
      {item}
    </Combobox.Option>
  ))

  const showNewOption =
    !exactOptionMatch && search != null && search.trim().length > 0

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(newSelectedValue) => {
        if (newSelectedValue === '$create') {
          setSelectOptions((current) => [...current, search ?? ''])
          handleChange(search)
        } else if (newSelectedValue === currentValue && allowDeselect) {
          handleChange(null)
        } else {
          handleChange(newSelectedValue)
        }
        setSearch(null)

        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          required={required}
          rightSection={<Combobox.Chevron />}
          value={search ?? currentValue ?? ''}
          onChange={(event) => {
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
            setSearch(event.currentTarget.value)
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown()
            setSearch(null)
          }}
          placeholder={placeholder ?? 'Select...'}
          rightSectionPointerEvents="none"
          className={className}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options}
          {showNewOption && (
            <Combobox.Option value="$create">+ Add {search}</Combobox.Option>
          )}
        </Combobox.Options>
        {!showNewOption && (
          <Text c="dimmed" className="py-1 text-center text-sm">
            Search to add a new option
          </Text>
        )}
      </Combobox.Dropdown>
    </Combobox>
  )
}
